import { AccordionDetails, AccordionSummary, Collapse } from '@mui/material';
import type { Dispatch, SetStateAction } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { Or2Accordion } from '@/components/Surface/Or2Accordion';

import { graphql, type DocumentType } from '@/gql';

import { UploadingFilesTable } from './uploading-bar/UploadingFilesTable';
import { UploadSummary } from './uploading-bar/UploadSummary';

export type UploadingZipStatus =
  'uploading' | 'extracting' | 'creating' | 'done';

export interface UploadingFile {
  id: number;
  filename: string;
  uploadedBytes: number;
  totalBytes: number;
  parent: string | null | undefined;
  error?: string;
  startedAt: Date;
  zipStatus?: UploadingZipStatus;
}

export interface UploadingFolder {
  id: number;
  filename: string;
  parent: string | null | undefined;
  error?: string;
  created: boolean;
  startedAt: Date;
}

export interface FileManagerUploadingState {
  uploadingFiles: UploadingFile[];
  uploadingFolders: UploadingFolder[];
  uploadingZipFiles: UploadingFile[];
}

const FILE_MANAGER_UPLOADING_BAR_FILES_FOR_OWNER_FRAGMENT = graphql(`
  fragment fileManagerUploadingBarFilesForOwnerFields on FilesForOwner {
    ...uploadingFileTableFilesForOwnerFields
  }
`);

interface Props {
  filesForOwner: DocumentType<
    typeof FILE_MANAGER_UPLOADING_BAR_FILES_FOR_OWNER_FRAGMENT
  >;
  uploadingState: FileManagerUploadingState;
  setUploadingState: Dispatch<SetStateAction<FileManagerUploadingState>>;
}

export const FileManagerUploadingBar = ({
  filesForOwner,
  uploadingState,
  setUploadingState,
}: Props) => {
  const { uploadingFiles, uploadingFolders, uploadingZipFiles } =
    uploadingState;

  const isVisible =
    uploadingFiles.length + uploadingFolders.length + uploadingZipFiles.length >
    0;

  return (
    <Collapse in={isVisible}>
      <Or2Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <UploadSummary
            uploadingState={uploadingState}
            setUploadingState={setUploadingState}
          />
        </AccordionSummary>
        <AccordionDetails>
          <UploadingFilesTable
            filesForOwner={filesForOwner}
            uploadingState={uploadingState}
            setUploadingState={setUploadingState}
          />
        </AccordionDetails>
      </Or2Accordion>
    </Collapse>
  );
};
